import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { ValidationErrorService } from 'src/app/core/services/error/validation-error/validation-error.service';
import { Subscription } from 'rxjs';
import { TranslationService } from 'src/app/core/services/translation/translation.service';

@Component({
  selector: 'app-number-manual-increment',
  templateUrl: './number-manual-increment.component.html',
  styleUrls: ['./number-manual-increment.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Component for the number manual increment
 */
export class NumberManualIncrementComponent implements OnInit, OnDestroy {
  @Input() control!: FormControl;
  @Input() min!: number;
  @Input() max!: number;
  @Input() suffix: string = 'times';
  @Input() resetButtonText: string = 'Remove limit';
  @Input() fieldName: string = '';
  @Input() errorMessage: string = '';
  @Output() resetField = new EventEmitter<void>();
  translatedFieldName = '';

  maxLength!: number;
  private subscription = new Subscription();

  /**
   * @constructor
   */
  constructor(
    private validationErrorService: ValidationErrorService,
    private translationService: TranslationService
  ) {}

  /**
   * OnInit
   */
  ngOnInit(): void {
    this.maxLength = this.validationErrorService.getMaximumLength(this.control);

    this.setErrorMessage(this.control);
    this.subscription.add(
      this.control.statusChanges.subscribe(() => {
        this.setErrorMessage(this.control);
      })
    );

    // Subscribe to value changes to enforce maxLength
    this.subscription.add(
      this.control.valueChanges.subscribe((value) => {
        if (value && value.toString().length > this.maxLength) {
          const truncatedValue = parseInt(
            value.toString().slice(0, this.maxLength)
          );
          this.control.setValue(truncatedValue, { emitEvent: false });
        }
      })
    );

    if (this.fieldName) {
      this.translationService
        .translateString(this.fieldName)
        .then((translated) => {
          this.translatedFieldName = translated;
        });
    }
  }

  /**
   * OnDestroy
   */
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Only allows numeric input
   * @param {KeyboardEvent} event - The keyboard event
   * @return {boolean} Returns true if the input is a number, false otherwise
   */
  onlyNumbers(event: KeyboardEvent): boolean {
    return (
      /^\d$/.test(event.key) ||
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
      ].includes(event.key)
    );
  }

  /**
   * Sets error message of this input
   * @param {AbstractControl} abstractControl
   */
  setErrorMessage(abstractControl: AbstractControl) {
    this.validationErrorService
      .translateError(abstractControl, '', this.translatedFieldName)
      .then((errorMessage) => {
        this.errorMessage = errorMessage;
      });
  }

  /**
   * Handles paste events and ensures only numbers are pasted
   * @param {ClipboardEvent} event - The paste event
   */
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedInput = event.clipboardData?.getData('text/plain');

    if (pastedInput) {
      const numericValue = pastedInput.replace(/\D/g, '');

      if (numericValue) {
        const numValue = parseInt(numericValue);
        if (!isNaN(numValue)) {
          const boundedValue = Math.min(Math.max(numValue, this.min), this.max);
          this.control.setValue(boundedValue);
        }
      }
    }
  }

  /**
   * Increments the value of the control
   */
  increment(): void {
    const currentValue = this.control.value || 0;
    if (currentValue < this.max) {
      this.control.setValue(currentValue + 1, { emitEvent: true });
      this.control.markAsDirty();
    }
  }

  /**
   * Decrements the value of the control
   */
  decrement(): void {
    const currentValue = this.control.value || 0;
    if (currentValue > this.min) {
      this.control.setValue(currentValue - 1, { emitEvent: true });
      this.control.markAsDirty();
    }
  }

  /**
   * Resets the value of the control
   */
  resetValue(): void {
    this.control.reset();
    this.resetField.emit();
  }
}
